// ...imports remain the same
import React, { useEffect, useState } from "react";
import Form from "react-bootstrap/Form";
import { Row, Col } from "react-bootstrap";
import InputWithLabel from "../../components/shared/fields/InputWithLabel";
import Button from "../../components/shared/buttons/button";
import { showToast } from "../../helpers/Utils";
import {
  useUpdateSurveyMutation,
  useGetSurveyByIdQuery,
  useUploadImageMetadataMutation,
} from "../../app/surveyApi/surveyApi";
import QuestionBlock from "./QuestionBlock";
import { useNavigate, useLocation, Link } from "react-router";
import "./survey.css";
import CommonBreadcrumb from "../../components/shared/breadcrumb/CommonBreadcrumb";
import SelectBox from "../../components/shared/fields/SelectBox";

const EditSurvey = () => {
  const { state } = useLocation();
  const surveyId = state?.surveyId;
  const cateId = state?.cateId;
  const navigate = useNavigate();

  const { data: getSingleSurvey } = useGetSurveyByIdQuery(surveyId, {
    skip: !surveyId,
  });
  console.log(getSingleSurvey, "getSingleSurvey");

  const [updateSurvey, { isLoading }] = useUpdateSurveyMutation();
  const [uploadImageMetadata, { isLoading: isUploadingImages }] = useUploadImageMetadataMutation();
  const [pdfImage, setPdfImage] = useState(null)
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [questions, setQuestions] = useState([]);
  console.log(questions, "questions--->");

  useEffect(() => {
    if (getSingleSurvey?.data) {
      const data = getSingleSurvey.data;
      setPdfImage(data?.pdf)
      setTitle(data.title || "");
      setDescription(data.description || "");

      const formattedQuestions = (data.questions || []).map((q) => {
        const formatted = {
          text: q.text,
          type: q.questionType,
          isRepeatable: q.isRepeatable || false,
          subQuestions: (q.subQuestions || []).map(subQ => {
            const formattedSubQ = {
              text: subQ.text || "",
              type: subQ.questionType || subQ.type || "text",
              isCompulsory: subQ.isCompulsory ?? true,
              options: subQ.options || [],
              imageUrl: subQ.imageUrl || null,
              imageFile: null,
              tooltip: subQ.tooltip,
              showCommentBox: subQ.showCommentBox || false
            };

            // Handle options for subquestions with select types
            if (["singleSelect", "multiSelect"].includes(formattedSubQ.type)) {
              formattedSubQ.options = subQ.options?.map((opt) => {
                if (typeof opt === 'object' && opt.label) {
                  return opt.label;
                }
                return opt || "";
              }) || [""];
            }

            return formattedSubQ;
          }),
          options: q.options || [],
          isCompulsory: q.isCompulsory,
          imageUrl: q.imageUrl || null,
          imageFile: null,
          tooltip: q.tooltip,
          showCommentBox: q.showCommentBox || false
        };

        if (["singleSelect", "multiSelect"].includes(q.questionType)) {
          // Handle both object format {value, label, _id} and string format
          formatted.options = q.options?.map((opt) => {
            if (typeof opt === 'object' && opt.label) {
              return opt.label;
            }
            return opt || "";
          }) || [""];
        }

        if (q.questionType === "range") {
          formatted.min = q.min ?? 1;
          formatted.max = q.max ?? 10;
        }

        return formatted;
      });

      setQuestions(formattedQuestions);
    }
  }, [getSingleSurvey]);

  const handleQuestionChange = (index, field, value) => {
    const updated = [...questions];
    updated[index][field] = value;

    if (field === "isRepeatable") {
      updated[index].isRepeatable = value;
      updated[index].questionType = value ? "number" : updated[index].questionType;
      if (value && !updated[index].subQuestions) {
        updated[index].subQuestions = [];
      }
      if (!value) {
        delete updated[index].subQuestions;
      }
    }
    if (field === "showCommentBox") {
      updated[index].showCommentBox = value;
    }
    if (field === "tooltip") {
      updated[index].tooltip = value;
    }
    if (field === "isCompulsory") {
      updated[index].isCompulsory = value;
    }

    if (field === "type") {
      if (["singleSelect", "multiSelect"].includes(value)) {
        // Create a new mutable array to avoid extensibility issues
        // Ensure there's always at least one default option
        if (updated[index].options && Array.isArray(updated[index].options) && updated[index].options.length > 0) {
          updated[index].options = [...updated[index].options];
        } else {
          updated[index].options = [""];
        }
        delete updated[index].min;
        delete updated[index].max;
      } else if (value === "range") {
        updated[index].min = 1;
        updated[index].max = 10;
        delete updated[index].options;
      } else {
        delete updated[index].options;
        delete updated[index].min;
        delete updated[index].max;
      }

      if (value === "number" && updated[index].isRepeatable) {
        updated[index].subQuestions = updated[index].subQuestions ? [...updated[index].subQuestions] : [];
      }
    }

    setQuestions(updated);
  };

  const handleOptionChange = (qIndex, oIndex, value) => {
    const updated = [...questions];
    // Create a new mutable array to avoid extensibility issues
    const newOptions = [...updated[qIndex].options];
    newOptions[oIndex] = value;
    updated[qIndex].options = newOptions;
    setQuestions(updated);
  };

  const addOption = (qIndex) => {
    const updated = [...questions];
    if (!Array.isArray(updated[qIndex].options)) {
      updated[qIndex].options = [];
    }

    const hasBlank = updated[qIndex].options.some((opt) => !opt.trim());
    if (hasBlank) {
      showToast("Option should not be blank.", "error");
      return;
    }

    // Create a new mutable array to avoid extensibility issues
    updated[qIndex].options = [...updated[qIndex].options, ""];
    setQuestions(updated);
  };

  const removeOption = (qIndex, oIndex) => {
    const updated = [...questions];
    // Create a new mutable array to avoid extensibility issues
    updated[qIndex].options = updated[qIndex].options.filter((_, index) => index !== oIndex);
    setQuestions(updated);
  };

  const handleRangeChange = (index, field, value) => {
    const updated = [...questions];
    updated[index][field] = Number(value);
    setQuestions(updated);
  };

  const removeQuestion = (index) => {
    const updated = [...questions];
    updated.splice(index, 1);
    setQuestions(updated);
  };

  const addQuestion = () => {
    const hasBlank = questions.some((q) => !q.text || !q.text.trim());
    if (hasBlank) {
      showToast("Question title should not be blank.", "error");
      return;
    }

    setQuestions([
      ...questions,
      { text: "", type: "text", isRepeatable: false, isCompulsory: true, imageUrl: null, imageFile: null , tooltip:"", showCommentBox: false },
    ]);
  };

  const handleSubQuestionChange = (qIndex, subIndex, field, value) => {
    const updated = [...questions];
    updated[qIndex].subQuestions[subIndex][field] = value;

    // Handle type changes for subquestions to ensure default options
    if (field === "type") {
      if (["singleSelect", "multiSelect"].includes(value)) {
        // Ensure there's always at least one default option
        if (updated[qIndex].subQuestions[subIndex].options &&
          Array.isArray(updated[qIndex].subQuestions[subIndex].options) &&
          updated[qIndex].subQuestions[subIndex].options.length > 0) {
          updated[qIndex].subQuestions[subIndex].options = [...updated[qIndex].subQuestions[subIndex].options];
        } else {
          updated[qIndex].subQuestions[subIndex].options = [""];
        }
      } else if (value === "range") {
        updated[qIndex].subQuestions[subIndex].min = 1;
        updated[qIndex].subQuestions[subIndex].max = 10;
        delete updated[qIndex].subQuestions[subIndex].options;
      } else {
        delete updated[qIndex].subQuestions[subIndex].options;
        delete updated[qIndex].subQuestions[subIndex].min;
        delete updated[qIndex].subQuestions[subIndex].max;
      }
    }

    setQuestions(updated);
  };

  const addSubQuestion = (qIndex) => {
    const updated = JSON.parse(JSON.stringify(questions));

    if (!Array.isArray(updated[qIndex].subQuestions)) {
      updated[qIndex].subQuestions = [];
    }

    const hasBlank = updated[qIndex].subQuestions.some(
      (subQ) => !subQ.text || !subQ.text.trim()
    );

    if (hasBlank) {
      showToast("Subquestion title should not be blank.", "error");
      return;
    }

    updated[qIndex].subQuestions.push({
      text: "",
      type: "text",
      isCompulsory: true,
      imageUrl: null,
      imageFile: null,
      tooltip : "",
      showCommentBox: false
    });
    setQuestions(updated);
  };

  const removeSubQuestion = (qIndex, subIndex) => {
    const updated = [...questions];
    updated[qIndex].subQuestions.splice(subIndex, 1);
    setQuestions(updated);
  };

  const handleImageChange = (index, file) => {
    const updated = [...questions];
    updated[index].imageFile = file;
    // Clear existing imageUrl if a new file is selected (for replacement)
    if (file) {
      updated[index].imageUrl = null;
    }
    setQuestions(updated);
  };

  const handleSubQuestionImageChange = (qIndex, subIndex, file) => {
    const updated = [...questions];
    updated[qIndex].subQuestions[subIndex].imageFile = file;
    // Clear existing imageUrl if a new file is selected (for replacement)
    if (file) {
      updated[qIndex].subQuestions[subIndex].imageUrl = null;
    }
    setQuestions(updated);
  };

  const transformQuestion = (q) => {
    const base = {
      text: q.text?.trim() || "",
      questionType: q.type,
      isRepeatable: q.isRepeatable || false,
      isCompulsory: q.isCompulsory ?? true,
      tooltip: q.tooltip,
      showCommentBox: q.showCommentBox || false
    };

    // Include imageUrl if it exists
    if (q.imageUrl) {
      base.imageUrl = q.imageUrl;
    }

    // ✅ Validate & include options for select types
    if (["singleSelect", "multiSelect"].includes(q.type)) {
      const filteredOptions = q.options
        ?.filter((opt) => opt && opt.trim())
        .map((opt) => ({
          value: opt.toLowerCase().replace(/\s+/g, "-"),
          label: opt.trim(),
        }));

      // Backend requires options for select types
      if (!filteredOptions?.length) {
        throw new Error(`Options are required for "${q.text || "a question"}"`);
      }

      base.options = filteredOptions;
    }

    // ✅ Include range limits only for range type
    if (q.type === "range") {
      base.min = q.min ?? 1;
      base.max = q.max ?? 10;
    }

    // ✅ Recursively handle subQuestions
    if (q.subQuestions && q.subQuestions.length > 0) {
      base.subQuestions = q.subQuestions.map(transformQuestion);
    }

    return base;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!title.trim() || !description.trim()) {
      showToast("Title and description are required.", "error");
      return;
    }

    try {
      let updatedQuestions = [...questions];

      // Step 1: Collect all new images (main questions and subquestions)
      const allImages = [];
      const imageMapping = [];

      questions.forEach((q, qIndex) => {
        if (q.imageFile) {
          allImages.push(q.imageFile);
          imageMapping.push({ type: 'main', qIndex });
        }
        
        if (q.subQuestions && Array.isArray(q.subQuestions)) {
          q.subQuestions.forEach((subQ, subIndex) => {
            if (subQ.imageFile) {
              allImages.push(subQ.imageFile);
              imageMapping.push({ type: 'sub', qIndex, subIndex });
            }
          });
        }
      });
      
      // Step 2: Upload new images if any
      if (allImages.length > 0) {
        showToast("Uploading new images...", "info");
        
        const imageFormData = new FormData();
        allImages.forEach((imageFile, idx) => {
          imageFormData.append(`image${idx + 1}`, imageFile);
        });

        const imageUploadResponse = await uploadImageMetadata({ body: imageFormData }).unwrap();
        
        if (imageUploadResponse.type === "success") {
          showToast("Images uploaded successfully!", "success");
          
          // Step 3: Map uploaded image URLs to corresponding questions and subquestions
          const imageUrls = imageUploadResponse.data.images;
          
          updatedQuestions = questions.map((q, qIndex) => {
            let updatedQ = { ...q };
            
            // Handle main question images
            const mainImageIndex = imageMapping.findIndex(
              mapping => mapping.type === 'main' && mapping.qIndex === qIndex
            );
            if (mainImageIndex !== -1) {
              const imageKey = `image${mainImageIndex + 1}`;
              updatedQ.imageUrl = imageUrls[imageKey];
            }
            
            // Handle subquestion images
            if (updatedQ.subQuestions && Array.isArray(updatedQ.subQuestions)) {
              updatedQ.subQuestions = updatedQ.subQuestions.map((subQ, subIndex) => {
                const subImageIndex = imageMapping.findIndex(
                  mapping => mapping.type === 'sub' && 
                            mapping.qIndex === qIndex && 
                            mapping.subIndex === subIndex
                );
                if (subImageIndex !== -1) {
                  const imageKey = `image${subImageIndex + 1}`;
                  return { ...subQ, imageUrl: imageUrls[imageKey] };
                }
                return subQ;
              });
            }
            
            return updatedQ;
          });
        } else {
          showToast("Failed to upload images", "error");
          return;
        }
      }

      // Step 4: Validate and transform all questions (throws if missing required options)
      const formatted = updatedQuestions.map(transformQuestion);

      // Step 5: Build FormData (for FormData backend)
      const formData = new FormData();
      formData.append("title", title);
      formData.append("description", description);
      formData.append("questions", JSON.stringify(formatted));

      if (pdfImage) {
        formData.append("pdf", pdfImage)
      }

      await updateSurvey({ surveyId, body: formData }).unwrap();
      showToast("Survey updated successfully!", "success");
      navigate(`/survey-category/survey/${cateId}`);
    } catch (err) {
      console.error("Error in form submission:", err);
      if (err.message?.includes("Options are required")) {
        showToast(err.message, "error");
      } else {
        showToast(err?.data?.message || "Failed to update survey", "error");
      }
    }
  };


  const breadcrumbItems = [
    { label: "Survey category", path: "/" },
    { label: "Survey", path: `/survey-category/survey/${cateId}` },
    { label: "Edit Survey" },
  ];

  return (
    <Row className="my-4">
      <Col lg={12}>
        <CommonBreadcrumb items={breadcrumbItems} />
      </Col>
      <Col lg={8} className="m-auto">
        <h2 className="cmn_title">Update Survey</h2>
        <Form onSubmit={handleSubmit}>
          <InputWithLabel
            label="Survey Title"
            placeholder="Enter survey title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
          <SelectBox label="Survey Category" value={'test'} disabled={true} options={[{ label: "test", value: "test" }]} />
          <Form.Group className="mb-3">
            <Form.Label>Survey Description</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Enter description"
            />
          </Form.Group>


          <Form.Group className="mb-3">
            <div className="d-flex gap justify-content-between align-items-center">
              <Form.Label>Add PDF file for survey</Form.Label>
              {pdfImage !==  null &&     <a  className="highlight" href={pdfImage} target="_blank" rel="noopener noreferrer">
            View PDF
          </a>}
            </div>
            <Form.Control
              type="file"
              accept="image/*"
              onChange={(e) => setPdfImage(e.target.files[0])}
            />
          </Form.Group>
  
          <div className="d-flex gap-2 flex-column">
            {questions.map((q, index) => (
              <QuestionBlock
                key={index}
                q={q}
                index={index}
                handleQuestionChange={handleQuestionChange}
                handleOptionChange={handleOptionChange}
                addOption={addOption}
                removeOption={removeOption}
                handleRangeChange={handleRangeChange}
                removeQuestion={removeQuestion}
                questionsLength={questions.length}
                handleSubQuestionChange={handleSubQuestionChange}
                addSubQuestion={addSubQuestion}
                removeSubQuestion={removeSubQuestion}
                handleImageChange={handleImageChange}
                handleSubQuestionImageChange={handleSubQuestionImageChange}
              />
            ))}
          </div>

          <div className="d-flex justify-content-end gap-3 mt-3">
            <Button
              onClick={addQuestion}
              size="medium"
              label="Add Question"
              className="borderedBtn"
              type="button"
            />
            <Button
              size="medium"
              type="submit"
              disabled={isLoading || isUploadingImages}
              label={isLoading || isUploadingImages ? "Updating..." : "Update Survey"}
            />
          </div>
        </Form>
      </Col>
    </Row>
  );
};

export default EditSurvey;
